import { debounce } from "lodash"
import EditorJS from "@editorjs/editorjs"
import Header from "@editorjs/header"
import Paragraph from "@editorjs/paragraph"
import Quote from "@editorjs/quote"
import List from "@editorjs/list"
import Table from "@editorjs/table"
import ImageTool from "@editorjs/image"
import Delimiter from "@editorjs/delimiter"
import Underline from "@editorjs/underline"
import Marker from "@editorjs/marker"
import Warning from "@editorjs/warning"
import Embed from "@editorjs/embed"

import Hook from "../utils/hook"
import { uploadToTusd } from "../utils/uploaders"

// the width and height of an image file as displayed (browsers apply its EXIF
// orientation), stored on the image block so the rendered page can reserve its
// space before the image loads; empty when they can't be read
function imageDimensions(file) {
  const url = URL.createObjectURL(file)
  const image = new Image()

  return new Promise((resolve) => {
    image.onload = () =>
      resolve(
        image.naturalWidth > 0 && image.naturalHeight > 0
          ? { width: image.naturalWidth, height: image.naturalHeight }
          : {}
      )
    image.onerror = () => resolve({})
    image.src = url
  }).finally(() => URL.revokeObjectURL(url))
}

function normalizeEditorjsData(data) {
  if (!data || !Array.isArray(data.blocks)) return data

  return {
    ...data,
    blocks: data.blocks.map((block) => {
      if (block?.type !== `image` || !block?.data) return block
      if (block.data.file?.url) return block
      if (!block.data.url) return block

      return {
        ...block,
        data: {
          ...block.data,
          file: {
            id: block.data.id || null,
            url: block.data.url,
          },
        },
      }
    }),
  }
}

export default Hook(`FormEditorjs`, {
  targets: [`input`, `editor`],
  values: {
    placeholder: String,
    imageUploadFinalizeEventName: String,
    imageUploadPrepareEventName: String,
    imageUploadByUrlEventName: String,
  },

  editor: null,
  observer: null,
  // onChangeHandlers: new Set(),

  mounted() {
    this.observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        this.observer.disconnect()
        this.load()
      }
    })

    this.observer.observe(this.el)
  },

  destroyed() {
    this.observer?.disconnect()
    this.editor?.destroy()
  },

  load() {
    const tools = {
      embed: {
        class: Embed,
        inlineToolbar: true,
        config: {
          services: {
            youtube: true,
            vimeo: true,
          },
        },
      },
      header: {
        class: Header,
        config: {
          levels: [2, 3],
          defaultLevel: 2,
        },
      },
      paragraph: {
        class: Paragraph,
        inlineToolbar: true,
      },
      table: {
        class: Table,
        inlineToolbar: true,
      },
      list: {
        class: List,
        inlineToolbar: true,
        config: {
          defaultStyle: `unordered`,
        },
      },
      quote: Quote,
      warning: Warning,
      Marker: Marker,
      delimiter: Delimiter,
      underline: Underline,
    }

    if (
      this.imageUploadPrepareEventNameValue ||
      this.imageUploadFinalizeEventNameValue ||
      this.imageUploadByUrlEventNameValue
    ) {
      tools.image = {
        class: ImageTool,
        config: {
          field: `file`,
          uploader: {
            uploadByFile:
              this.imageUploadPrepareEventNameValue &&
              this.imageUploadFinalizeEventNameValue &&
              ((file) => {
                const dimensions = imageDimensions(file)

                return this.pushEvent(this.imageUploadPrepareEventNameValue, {
                  name: file.name,
                  size: file.size,
                  type: file.type,
                })
                  .then((prepareReply) => {
                    if (
                      !prepareReply?.token ||
                      !prepareReply?.upload_url ||
                      !prepareReply?.headers ||
                      !prepareReply?.metadata ||
                      prepareReply?.uploader !== `s3_tusd`
                    ) {
                      throw new Error()
                    }

                    return uploadToTusd(file, prepareReply).then(() =>
                      this.pushEvent(this.imageUploadFinalizeEventNameValue, {
                        token: prepareReply.token,
                      })
                    )
                  })
                  .then((reply) => {
                    if (!reply?.url || !reply?.id) throw new Error()
                    return dimensions.then((size) => ({
                      file: { id: reply.id, url: reply.url, ...size },
                      success: 1,
                    }))
                  })
                  .catch(() => {
                    return { success: 0 }
                  })
              }),

            uploadByUrl:
              this.imageUploadByUrlEventNameValue &&
              ((url) => {
                return this.pushEvent(this.imageUploadByUrlEventNameValue, {
                  url,
                })
                  .then((reply) => {
                    if (!reply?.url || !reply?.id) throw new Error()
                    return {
                      file: {
                        id: reply.id,
                        url: reply.url,
                        // known when the image's metadata has them
                        ...(reply.width &&
                          reply.height && {
                            width: reply.width,
                            height: reply.height,
                          }),
                      },
                      success: 1,
                    }
                  })
                  .catch(() => {
                    return { success: 0 }
                  })
              }),
          },
        },
      }
    }

    this.editor = new EditorJS({
      holder: this.editorTarget,
      data: normalizeEditorjsData(JSON.parse(this.inputTarget.value)),
      placeholder: this.placeholderValue,
      tools,
      onReady: () => {
        // disables `narrow` mode
        const narrowNode = this.editorTarget.querySelector(
          `.codex-editor--narrow`
        )
        if (narrowNode) this.classRemove(`codex-editor--narrow`, narrowNode)
      },
      onChange: debounce((api) => {
        api.saver.save().then((data) => {
          const value = JSON.stringify(data).replaceAll(`&nbsp;`, ``)
          this.inputTarget.value = value
          this.inputTarget.dispatchEvent(new Event(`change`, { bubbles: true }))
          // this.onChangeHandlers.forEach((handler) => handler(value))
        })
      }, 200),
    })
  },

  // addOnChangeHandler(handler) {
  //   this.onChangeHandlers.add(handler)
  // },
})
